<script setup lang="ts">
/**
 * @author Giorgi Tseradze
 */

import { onMounted, ref } from "vue";

import { isInView } from "@/composables/utils/isInView";

import PercentageCard from "@/components/smartComponents/ConstructionStages/parts/PercentageCard.vue";
import DateCard from "@/components/smartComponents/ConstructionStages/parts/DateCard.vue";

import { ListDataInterface } from "@/types/ContentType";

const Picture = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Picture.vue")
);

const props = defineProps<{
  contentData: ListDataInterface;
  nextActive: boolean;
  prevActive: boolean;
}>();

const emit = defineEmits(["prev", "next"]);

const keyVal = ref(0);
const firstRow = ref();
const secondRow = ref();
const thirdRow = ref();
const secondDelay = ref();
const thirdDelay = ref();
const firstTrigger = ref(false);
const secondTrigger = ref(false);
const thirdTrigger = ref(false);

const handleDelay = () => {
  if (props?.contentData?.row_1?.length === 1) {
    secondDelay.value = 2;
  }
  if (props?.contentData?.row_1?.length === 2) {
    secondDelay.value = 3;
  }
  if (props?.contentData?.row_1?.length === 3) {
    secondDelay.value = 4;
  }
  if (secondDelay?.value === 2) {
    thirdDelay.value = 3;
  }
  if (secondDelay?.value === 3) {
    thirdDelay.value = 4;
  }
  if (secondDelay?.value === 4) {
    thirdDelay.value = 5;
  }
};

const toggleAnimation = () => {
  firstRow?.value?.classList.remove("is-inview");
  firstRow?.value?.classList.add("is-inview");
  secondRow?.value?.classList.remove("is-inview");
  secondRow?.value?.classList.add("is-inview");
  thirdRow?.value?.classList.remove("is-inview");
  thirdRow?.value?.classList.add("is-inview");
};

const handlePrev = () => {
  emit("prev");
  keyVal.value++;
  setTimeout(() => {
    handleDelay();
    toggleAnimation();
  }, 450);
};

const handleNext = () => {
  emit("next");
  keyVal.value++;
  setTimeout(() => {
    handleDelay();
    toggleAnimation();
  }, 450);
};

const handleIsTrigger = () => {
  firstTrigger.value = isInView(firstRow.value!, 1);
  secondTrigger.value = isInView(secondRow.value!, 1);
  thirdTrigger.value = isInView(thirdRow.value!, 1);
};

onMounted(() => {
  handleDelay();

  setTimeout(() => {
    window?.addEventListener("scroll", handleIsTrigger);
  }, 2000);
});

onUnmounted(() => {
  window?.removeEventListener("scroll", handleIsTrigger);
});
</script>

<template>
  <Transition name="fade" mode="out-in">
    <section class="big-box" :key="keyVal">
      <div ref="firstRow" :class="firstTrigger ? 'is-inview' : ''" class="first-row">
        <DateCard
          :date="contentData?.date"
          :prevActive="prevActive"
          :nextActive="nextActive"
          @prev="handlePrev"
          @next="handleNext"
          class="first-row__date"
        />
        <div
          class="first-row__image pos-rel"
          v-for="(photo, index) in contentData?.row_1"
          :key="index"
          :style="`transition-delay: ${(1 + index) * 400}ms;`"
        >
          <Picture :devices="photo?.devices" class="img-absolute" alt="construction" />
        </div>
      </div>
      <div ref="secondRow" :class="secondTrigger ? 'is-inview' : ''" class="second-row">
        <div
          class="second-row__image pos-rel"
          v-for="(photo, index) in contentData?.row_2"
          :key="index"
          :style="`transition-delay: ${(secondDelay + index) * 400}ms;`"
        >
          <Picture :devices="photo?.devices" class="img-absolute" alt="construction" />
        </div>
        <PercentageCard
          v-if="contentData?.row_3?.length === 0"
          :contentData="contentData?.percent"
          class="second-row__percent"
          :style="`transition-delay: ${
            (secondDelay + contentData?.row_2?.length) * 400
          }ms;`"
        />
      </div>
      <div
        v-show="contentData?.row_3?.length !== 0"
        ref="thirdRow"
        :class="thirdTrigger ? 'is-inview' : ''"
        class="third-row"
      >
        <div
          class="third-row__image pos-rel"
          v-for="(photo, index) in contentData?.row_3"
          :key="index"
          :style="`transition-delay: ${(thirdDelay + index + 1) * 400}ms;`"
        >
          <Picture :devices="photo?.devices" class="img-absolute" alt="construction" />
        </div>
        <PercentageCard
          v-if="contentData?.row_3?.length !== 0"
          :contentData="contentData?.percent"
          class="third-row__percent"
          :style="`transition-delay: ${
            (thirdDelay + contentData?.row_3?.length + 1) * 400
          }ms;`"
        />
      </div>
    </section>
  </Transition>
</template>

<style lang="scss"></style>
